import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useShops } from "../../../../hooks/useShops";
import { useDepartments } from "../../../../hooks/useDepartments";
import { all_routes } from "../../../../router/all_routes";
import {
  LeaveType,
  LeaveStatus,
  LeaveAdminUpdateDTO,
  LeaveDto,
  LeaveAdminFullUpdateDTO,
} from "../../../../types/leaves.types";
import { useLeaves } from "../../../../hooks/useLeaves";
import { message, Modal } from "antd";
import LeaveStatsComponent from "../components/LeaveStats";
import { LeaveTable } from "../components/LeaveTable";
import { isDateInRange } from "../../../../utils/date";
import dayjs from "dayjs";
import LeaveFilters from "../components/LeaveFilters";
import AdminLeaveModal from "../components/AdminLeaveModal";
import AdminLeaveEditModal from "../../../shared/modals/AdminLeaveEditModal";
import "jspdf-autotable";
import LoadingSpinner from "../../../shared/uiInterface/base-ui/LoadingSpinner";
import {
  handleExportExcelLeave,
  createPdf,
  normalizeTurkishChars,
} from "../../../../utils/exportUtils";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../../utils/errorHandler";
import FooterSection from "../../../shared/components/footerSection";
import { PermissionType } from "../../../../types/user.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useCompanyData } from "../../../../hooks/useCompanyData";
const SEGMENTED_LEAVE_INFO_TEXT =
  "Seçtiğiniz tarih aralığındaki çalışma dışı günler (hafta sonu/resmi tatil) otomatik olarak çıkarıldı. İzin talebiniz yalnızca çalışma günleri için oluşturuldu.";

const LeaveAdmin2 = () => {
  const {
    leaves,
    loading,
    leavesError,
    stats,
    fetchLeaves,
    updateLeaveStatus,
    createLeave,
    updateLeaveByAdmin,
    cancelLeave,
  } = useLeaves();
  const { companyData } = useCompanyData();
  const [selectedStatus, setSelectedStatus] = useState<LeaveStatus | "all">(
    "all"
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<LeaveType | "all">("all");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const { shops, error: shopsError } = useShops();
  const { departments, error: departmentsError } = useDepartments();
  const [selectedShop, setSelectedShop] = useState<string | null>("all");
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(
    "all"
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedLeave, setSelectedLeave] = useState<LeaveDto | undefined>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [leaveToEdit, setLeaveToEdit] = useState<LeaveDto | null>(null);
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  useEffect(() => {
    if (leavesError) {
      setError(leavesError.message);
    }
    if (shopsError) {
      setError(shopsError.message);
    }
    if (departmentsError) {
      setError(departmentsError.message);
    }
  }, [leavesError, shopsError, departmentsError]);
  const handleOpenLeaveModal = () => {
    setModalVisible(true);
  };

  const handleCloseLeaveModal = () => {
    setModalVisible(false);
    setSelectedLeave(undefined);
  };

  const handleReset = () => {
    setSelectedType("all");
    setDateRange(null);
    setSelectedDepartment("all");
    setSelectedShop("all");
    setSelectedStatus("all");
    fetchLeaves();
  };

  const handleSubmit = async (values: any) => {
    try {
      let segmentedByWorkingDays = false;
      if (selectedLeave) {
        // Düzenleme
        await updateLeaveStatus(selectedLeave.id, values);
      } else {
        // Yeni oluşturma
        const createResponse = await createLeave(values);
        segmentedByWorkingDays = !!createResponse?.segmentedByWorkingDays;
        if (createResponse?.segmentedByWorkingDays) {
          Modal.info({
            title: "Bilgilendirme",
            content: SEGMENTED_LEAVE_INFO_TEXT,
            okText: "Tamam",
            width: 480,
            centered: true,
          });
        }
      }
      if (selectedLeave) {
        message.success(t("leaves.modals.updated"));
      } else {
        if (!segmentedByWorkingDays) {
          message.success(t("leaves.modals.created"));
        }
      }
      handleCloseLeaveModal();
      fetchLeaves(); // Listeyi yenile
    } catch (error) {
      handleApiError(error, t);
    }
  };

  const filteredLeaves = useMemo(() => {
    if (!leaves) return [];

    return leaves.filter((leave) => {
      // İzin türü filtresi
      if (selectedType !== "all" && leave.type !== selectedType) {
        return false;
      }

      // Departman filtresi
      if (
        selectedDepartment !== "all" &&
        leave.user?.department
          ?.map((d) => d.id)
          .includes(Number(selectedDepartment))
      ) {
        return false;
      }
      if (selectedStatus !== "all" && leave.status !== selectedStatus) {
        return false;
      }
      // Şube filtresi
      if (
        selectedShop !== "all" &&
        leave.user?.branch?.map((b) => b.id).includes(Number(selectedShop))
      ) {
        return false;
      }

      // Tarih aralığı filtresi
      if (dateRange) {
        const leaveDate = dayjs(leave.startDate);
        const [startDate, endDate] = dateRange;

        if (!isDateInRange(leaveDate.toDate(), startDate, endDate)) {
          return false;
        }
      }

      return true;
    });
  }, [
    leaves,
    selectedType,
    selectedDepartment,
    selectedShop,
    dateRange,
    selectedStatus,
  ]);

  const handleStatusChange = async (
    leaveId: number,
    dto: LeaveAdminUpdateDTO
  ) => {
    try {
      await updateLeaveStatus(leaveId, dto);
      message.success(t("leaves.modals.statusUpdated"));
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleEditLeave = (leave: LeaveDto) => {
    setLeaveToEdit(leave);
    setEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setLeaveToEdit(null);
  };

  const handleUpdateLeave = async (
    id: number,
    data: LeaveAdminFullUpdateDTO
  ) => {
    try {
      await updateLeaveByAdmin(id, data);
      message.success("İzin başarıyla güncellendi");
      setEditModalVisible(false);
      setLeaveToEdit(null);
      fetchLeaves();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{t("leaves.pageTitle")}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    {t("leaves.breadcrumb.employee")}
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t("leaves.breadcrumb.leaves")}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {user?.permissions?.includes(PermissionType.EDIT_LEAVE) && (
              <button
                className="btn btn-sm btn-primary d-inline-flex align-items-center me-2"
                onClick={handleOpenLeaveModal}
              >
                <i className="ti ti-plus me-1" />
                {t("leaves.addLeave")}
              </button>
              )}
              {user?.permissions?.includes(PermissionType.EXPORT) && (
              <div className="dropdown me-3">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-file-export me-1" />
                  {t("common.buttons.export")}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-2 d-flex align-items-center py-2"
                      onClick={async () => {
                        // İzin verileri için sütun tanımları
                        type LeaveDataType = {
                          name: string;
                          department: string;
                          branch: string;
                          leaveType: string;
                          startDate: string;
                          endDate: string;
                          times: string;
                          status: string;
                          // reason: string;
                        };

                        // PDF için sütun tanımları
                        const columnsForPdf = [
                          {
                            header: "Ad Soyad",
                            property: "name" as keyof LeaveDataType,
                            width: 40,
                          },
                          {
                            header: "Departman",
                            property: "department" as keyof LeaveDataType,
                            width: 35,
                          },
                          {
                            header: "Şube",
                            property: "branch" as keyof LeaveDataType,
                            width: 35,
                          },
                          {
                            header: "İzin Türü",
                            property: "leaveType" as keyof LeaveDataType,
                            width: 30,
                          },
                          {
                            header: "Tarih Aralığı",
                            property: "dateRange" as keyof LeaveDataType,
                            width: 40,
                          },
                          {
                            header: "Saat",
                            property: "times" as keyof LeaveDataType,
                            width: 25,
                          },
                          {
                            header: "Durum",
                            property: "status" as keyof LeaveDataType,
                            width: 70,
                          },
                        ];

                        const leaveTypeTranslations: { [key: string]: string } = {
                          sick: "Hastalık İzni",
                          annual: "Yıllık İzin",
                          unpaid: "Ücretsiz İzin",
                          special: "Özel İzin",
                          hourly: "Saatlik İzin",
                          medical: "Sağlık İzni",
                          marriage: "Evlilik İzni",
                          birth: "Doğum İzni",
                          death: "Ölüm İzni",
                          paternity: "Babalık İzni",
                          casual: "Günübirlik İzin",
                          personal: "Kişisel İzin",
                          educational: "Eğitim İzni",
                        };

                        const getLeaveTypeText = (type: string): string => {
                          return leaveTypeTranslations[type] || type;
                        };

                        const getStatusText = (leave: LeaveDto): string => {
                            if (leave.approvalFlow && leave.approvalFlow.steps.length > 0) {
                              const steps = leave.approvalFlow.steps;
                              const allApproved = steps.every(s => s.status === 'approved');
                              if (allApproved) return "Tüm onaylar tamamlandı";
                        
                              for (const step of steps) {
                                const approverName = `${step.approver.firstName} ${step.approver.lastName}`;
                                if (step.status === 'rejected') return `${approverName} tarafından reddedildi`;
                                if (step.status === 'pending_approval' || step.status === 'pending') {
                                  return `${approverName} onayı bekleniyor (${step.stepOrder}/${steps.length})`;
                                }
                              }
                            }
                        
                            if (leave.status === LeaveStatus.APPROVED) {
                              const processor = leave.processedBy || leave.approvedBy;
                              return processor ? `${processor.firstName} ${processor.lastName} tarafından onaylandı` : "Onaylandı";
                            }
                        
                            if (leave.status === LeaveStatus.REJECTED) {
                              const processor = leave.processedBy || leave.rejectedBy;
                              return processor ? `${processor.firstName} ${processor.lastName} tarafından reddedildi` : "Reddedildi";
                            }
                            
                            if (leave.status === LeaveStatus.PENDING_HIERARCHY_APPROVAL) return "Onay Bekliyor";
                            if (leave.status === LeaveStatus.PENDING) return "Beklemede";
                        
                            return leave.status;
                          };
                        

                        // PDF için formatlanmış veri (Türkçe karakterler korunur - createPdf Roboto kullanır)
                        const formattedData = filteredLeaves.map((leave) => ({
                          name: leave.user
                            ? `${leave.user.firstName} ${leave.user.lastName}`
                            : "-",
                          department:
                            leave.user?.department
                              ?.map((d) => d.name)
                              .join(", ") || "-",
                          branch:
                            leave.user?.branch
                              ?.map((b) => b.name)
                              .join(", ") || "-",
                          leaveType: getLeaveTypeText(leave.type),
                          dateRange: `${dayjs(leave.startDate).format("DD.MM.YYYY")} - ${dayjs(leave.endDate).format("DD.MM.YYYY")}`,
                          times:
                            leave.startTime && leave.endTime
                              ? `${leave.startTime} - ${leave.endTime}`
                              : "-",
                          status: getStatusText(leave),
                        }));

                        // Filtre açıklaması
                        let description = "İzin Listesi Raporu";

                        if (dateRange) {
                          description += ` - Tarih: ${dayjs(
                            dateRange[0]
                          ).format("DD.MM.YYYY")} - ${dayjs(
                            dateRange[1]
                          ).format("DD.MM.YYYY")}`;
                        }

                        if (
                          selectedDepartment !== "all" &&
                          departments.length > 0
                        ) {
                          const deptName = departments.find(
                            (d) => d.id.toString() === selectedDepartment
                          )?.name;
                          if (deptName)
                            description += ` - Departman: ${deptName}`;
                        }

                        if (selectedShop !== "all" && shops.length > 0) {
                          const shopName = shops.find(
                            (s) => s.id.toString() === selectedShop
                          )?.name;
                          if (shopName)
                            description += ` - Şube: ${shopName}`;
                        }

                        if (selectedType !== "all") {
                          description += ` - İzin Türü: ${getLeaveTypeText(selectedType)}`;
                        }
                        
                        const statusTranslations: { [key: string]: string } = {
                            pending: "Beklemede",
                            approved: "Onaylandı",
                            rejected: "Reddedildi",
                            pending_hierarchy_approval: "Onay Bekliyor"
                        }

                        if (selectedStatus !== "all") {
                          description += ` - Durum: ${statusTranslations[selectedStatus] || selectedStatus}`;
                        }

                        // createPdf fonksiyonunu çağır (Roboto font ile Türkçe karakter desteği)
                        await createPdf(formattedData, {
                          title: "İzin Listesi Raporu",
                          description: description,
                          headerColor: "#2980b9", // Koyu mavi
                          accentColor: "#3498db", // Mavi
                          columns: columnsForPdf,
                          companyName: companyData?.name || "DINOX",
                          showStatistics: false, // İstatistik kutuları gösterme
                          footer: {
                            text: `© ${new Date().getFullYear()} ${companyData?.name || "DINOX"} - Tüm Hakları Saklıdır`,
                            showPageNumbers: true,
                          },
                        });
                      }}
                    >
                      <i className="ti ti-file-type-pdf me-2 text-danger" />
                      {t("common.buttons.pdf")}
                    </Link>
                  </li>
                  <li className="mt-1">
                    <Link
                      to="#"
                      className="dropdown-item rounded-2 d-flex align-items-center py-2"
                      onClick={() => handleExportExcelLeave(filteredLeaves)}
                    >
                      <i className="ti ti-file-type-xls me-2 text-success" />
                      {t("common.buttons.excel")}
                    </Link>
                  </li>
                </ul>
              </div>
              )}
            </div>
          </div>
          <LeaveStatsComponent stats={stats} />
          <div className="card">
            <div className="card-body">
              {loading ? (
                <LoadingSpinner size="lg" color="primary" centered />
              ) : (
                <LeaveTable
                  leaves={filteredLeaves || []}
                  onStatusChange={handleStatusChange}
                  onEdit={handleEditLeave}
                  extraToolbar={
                    <LeaveFilters
                      selectedStatus={selectedStatus}
                      selectedType={selectedType}
                      selectedDateRange={dateRange}
                      selectedDepartment={selectedDepartment || "all"}
                      selectedShop={selectedShop || "all"}
                      departments={departments}
                      shops={shops}
                      onStatusChange={setSelectedStatus}
                      onTypeChange={setSelectedType}
                      onDateRangeChange={setDateRange}
                      onDepartmentChange={setSelectedDepartment}
                      onShopChange={setSelectedShop}
                      onReset={handleReset}
                    />
                  }
                />
              )}
              {leavesError && (
                <div className="alert alert-danger" role="alert">
                  {leavesError.message}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* /Leaves list */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>

      <AdminLeaveModal
        leave={selectedLeave}
        onSubmit={handleSubmit}
        onClose={handleCloseLeaveModal}
        open={modalVisible}
      />

      <AdminLeaveEditModal
        leaveData={leaveToEdit}
        open={editModalVisible}
        onCancel={handleCloseEditModal}
        onUpdate={handleUpdateLeave}
      />
    </>
  );
};

export default LeaveAdmin2;
